'use client';
import {getStrapiData} from '@/app/lib/api';
import {cn} from '@/lib/utils';
import {StrapiGrowthStory} from '@/types/strapi';
import {cloneDeep} from 'lodash';
import {useLocale, useTranslations} from 'next-intl';
import {useEffect, useRef, useState} from 'react';
import {observeFade} from '../helpers/observe-fade';
import {abstract} from '../helpers/utils';
import {LazyImage} from '../tools/LazyImage';
import ComingSoon from './ComingSoon';
import Url from './Url';

export default function GrowthStoryList(Prop: {moduleOption: any}) {
  const locale = useLocale();
  const {moduleOption} = Prop;
  const [searchResults, setSearchResults] = useState<StrapiGrowthStory[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const isLoadingRef = useRef(false);
  const [isLoadingAllResult, setIsLoadingAllResult] = useState(false);
  const t = useTranslations('GrowthStory');
  const filters = cloneDeep(moduleOption.filters ?? {});

  const growthStoriesGroup = [
    {label: t('all'), value: 'all'},
    {label: t('asia'), value: 'asia'},
    {label: t('europe'), value: 'europe'}
  ];
  const [growthStoriesFilter, setGrowthStoriesFilter] = useState({
    filterGroup: 'all'
  });

  const getSearchResult = async () => {
    if (isLoadingRef.current) {
      return;
    }
    setIsLoading(true);
    isLoadingRef.current = true;
    const strapiOption = {
      populate: 'thumbnail',
      filters,
      pagination: {
        page: currentPage,
        pageSize: 50
      },
      sort: {updatedAt: 'desc'}
    };

    const postData = await getStrapiData(
      locale,
      'growth-stories',
      strapiOption
    );
    if (!postData?.data || postData?.data?.length == 0) {
      setIsLoadingAllResult(true);
    }
    setSearchResults((prev) => {
      if (postData?.data) {
        return [...prev, ...postData?.data];
      }
      return [...prev, ...[]];
    });

    setIsLoading(false);
    isLoadingRef.current = false;
  };

  useEffect(() => {
    setTimeout(getSearchResult, 0);
  }, [currentPage]);

  const paddingMap = [
    'pt-[40px] md:pt-[95px] lg:pt-[160px]',
    'pt-[70px] md:pt-[199px] lg:pt-[220px]',
    'md:pt-[177px] lg:pt-[300px]',
    'md:pt-[320px]'
  ];

  const chunkArray = (array: any[], size: number) => {
    if (size <= 0) {
      throw new Error('Chunk size must be a positive integer.');
    }
    const chunks: StrapiGrowthStory[][] = [];
    for (let i = 0; i < size; i++) {
      chunks.push([]);
    }
    for (let i = 0; i < array.length; i++) {
      const chunkIdx: number = i % size;
      chunks[chunkIdx].push(array[i]);
    }
    return chunks;
  };

  const divRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          if (!isLoadingRef.current && !isLoadingAllResult) {
            setCurrentPage((prev) => prev + 1);
          }
        }
      },
      {threshold: 0.1}
    );

    if (divRef.current) {
      observer.observe(divRef.current);
    }
  }, []);

  const filteredResult = () =>
    searchResults.filter((result) => {
      if (growthStoriesFilter.filterGroup === 'all') {
        return true;
      }
      return result.group === growthStoriesFilter.filterGroup;
    });

  const growItem = (result: StrapiGrowthStory) => {
    const title =
      result.title.trim().length > 0 ? result.title.trim() : result.name.trim();
    const titleArr = title
      .split(/\n|<br.*>/)
      .map((a) => a.trim())
      .filter((a) => a.trim().length > 0);

    return (
      <div key={result.slug}>
        <Url
          href={'/growth-story/' + result.slug}
          className="group"
          data-fade=""
        >
          <div className="fade-in-up">
            <div className="mb-4 md:flex md:flex-cols-2 gap-2 md:items-start relative">
              <div
                className={cn(
                  'hidden group-hover:flex z-[10] absolute top-0 left-0 w-full h-full ',
                  'justify-center items-center ',
                  'text-sm md:text-base ubuntu'
                )}
                style={{
                  background:
                    'linear-gradient(180deg, #e1272629 30%, #e12726cc 130%)'
                }}
              >
                <div className="rounded-full bg-[#FFFFFF4D] py-2 px-4 font-[700] text-white backdrop-blur-sm fade-in-up">
                  {t('readMore')} →
                </div>
              </div>
              <LazyImage
                className="w-full aspect-[4/3] object-cover "
                src={result.thumbnail?.url}
              />
            </div>
            <div>
              <div
                className=" font-[700] group-hover:text-red-500 pb-2 leading-[22px]"
                dangerouslySetInnerHTML={{__html: titleArr.join(', ')}}
              ></div>
              <h2
                className={cn(
                  'text-sm text-[#757575] mb-4 line-clamp-3 font-[400] group-hover:text-[#000]'
                )}
              >
                {result.abstract ?? abstract(result.content, locale, 100)}
              </h2>
            </div>
          </div>
        </Url>
      </div>
    );
  };

  useEffect(() => {
    setTimeout(() => {
      observeFade();
    }, 100);
  }, [filteredResult]);

  return (
    <div className={cn('text-[20px]')}>
      <div>
        <div className="flex m-auto justify-center gap-2 md:gap-6 text-sm ubuntu">
          {filters?.post_type != 'community' &&
            growthStoriesGroup.map((a, i) => (
              <button
                key={i}
                className={cn(
                  'rounded-full h-[40px] md:h-[50px] md:px-8 flex-1 md:flex-none text-center',
                  growthStoriesFilter.filterGroup === a.value
                    ? 'bg-[#E22219] text-white font-[700]'
                    : 'bg-[#FCEFEC] hover:bg-[#FFD0CE] font-[400]'
                )}
                onClick={() => {
                  setGrowthStoriesFilter({filterGroup: a.value});
                }}
              >
                {a.label}
              </button>
            ))}
        </div>
        {!isLoading && filteredResult().length === 0 && <ComingSoon />}

        {filteredResult().length > 0 && (
          <div className={cn('hidden lg:grid lg:grid-cols-4 gap-8')}>
            {chunkArray(filteredResult(), 4).map((chunk, index) => (
              <div
                key={index}
                className={cn(
                  paddingMap[index],
                  'flex flex-col gap-8',
                  index % 4 === 0 && 'lg:order-2', // 1st col → 2nd col
                  index % 4 === 1 && 'lg:order-3', // 2nd col → 3rd col
                  index % 4 === 2 && 'lg:order-1', // 3rd col → 1st col
                  index % 4 === 3 && 'lg:order-4'
                )}
              >
                {chunk?.map((result: StrapiGrowthStory) => growItem(result))}
              </div>
            ))}
          </div>
        )}

        {filteredResult().length > 0 && (
          <div className={cn('hidden lg:hidden md:grid md:grid-cols-3 gap-8')}>
            {chunkArray(filteredResult(), 3).map((chunk, index) => (
              <div
                key={index}
                className={cn(
                  paddingMap[index],
                  'flex flex-col gap-8',
                  index % 3 === 1 && 'md:order-3', // 2nd col → 3rd col
                  index % 3 === 2 && 'md:order-1', // 3rd col → 1st col
                  index % 3 === 0 && 'md:order-2' // 1st col → 2nd col
                )}
              >
                {chunk?.map((result: StrapiGrowthStory) => growItem(result))}
              </div>
            ))}
          </div>
        )}

        {filteredResult().length > 0 && (
          <div className={cn('grid grid-cols-2 md:hidden gap-4')}>
            {chunkArray(filteredResult(), 2).map((chunk, index) => (
              <div
                key={index}
                className={cn(paddingMap[index], 'flex flex-col gap-4')}
              >
                {chunk?.map((result: StrapiGrowthStory) => growItem(result))}
              </div>
            ))}
          </div>
        )}

        {isLoading && (
          <div className="text-center w-full">
            <div className={cn(filteredResult().length <= 0 && 'pt-[50px]')}>
              <span
                className={cn(
                  'material-symbols thin animate-spin  text-[#eee]',
                  filteredResult().length > 0 ? 'text-[30px]' : 'text-[100px]'
                )}
              >
                progress_activity
              </span>
            </div>
          </div>
        )}

        <div ref={divRef} className="h-[100px] w-full"></div>
      </div>
    </div>
  );
}
